import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@seahorse/common';
import {
  ObjectDefinitionModel,
  ScopeType,
  SeahorseModule,
  UiModuleOverviewConfiguration,
} from '@seahorse/domain';
import {
  ShButtonComponent,
  ShFieldComponent,
  ShSelectComponent,
  ShToggleComponent,
} from '@seahorse/components';
import {
  DIALOG_DATA,
  DialogContent,
  DialogFooterComponent,
  DialogRef,
} from '@seahorse/shell';
import { OrganisationStoreService } from '../../../../shared/services/organisation-store.service';
import { ProxyServices } from '../../../../shared/services/proxy.service';
import { UiModuleOverviewConfigurationFormComponent } from '../../../ui-modules/components/ui-module-overview-configuration-form/ui-module-overview-configuration-form.component';
import { isOverviewConfigurationValid } from '../../../ui-modules/components/ui-module-overview-configuration-form/ui-module-overview-configuration-form.model';
import { SeahorseModuleService } from '../../services/seahorse-module.service';

/** The two steps of the dialog; the second only for a module with an overview configuration. */
export type SeahorseModuleFormStep = 'module' | 'overview';

/**
 * The body of the "Add module" dialog (`DialogService.open`): step 1 picks
 * the organisation and the Seahorse object, step 2 (when the module gets an
 * overview configuration) embeds the overview configuration form. The last
 * step saves the module; the dialog then closes with `ok` and the saved
 * module, or cancels (with an error message) when the save fails.
 */
@Component({
  selector: 'su-seahorse-module-form',
  templateUrl: './seahorse-module-form.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    DialogFooterComponent,
    ShButtonComponent,
    ShFieldComponent,
    ShSelectComponent,
    ShToggleComponent,
    UiModuleOverviewConfigurationFormComponent,
  ],
})
export class SeahorseModuleFormComponent
  implements DialogContent<void, SeahorseModule>
{
  readonly data = inject<void>(DIALOG_DATA);
  readonly dialogRef = inject<DialogRef<SeahorseModule>>(DialogRef);

  @ViewChild(UiModuleOverviewConfigurationFormComponent)
  overviewForm?: UiModuleOverviewConfigurationFormComponent;

  private readonly _orgStore = inject(OrganisationStoreService);
  private readonly _proxyService = inject(ProxyServices);
  private readonly _seahorseModuleService = inject(SeahorseModuleService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _translateService = inject(TranslateService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly seahorseModule: SeahorseModule = SeahorseModule.default();

  // Stable fields, not getters: they feed PrimeNG inputs.
  readonly organisations = this._orgStore
    .getAll()
    .map((org) => ({ name: org.name, id: org.id.toString() }));
  seahorseObjects: ObjectDefinitionModel[] = [];

  hasOverviewConfiguration = false;
  step: SeahorseModuleFormStep = 'module';
  saveStarted = false;

  get isFirst(): boolean {
    return this.step === 'module';
  }

  get isLast(): boolean {
    return this.step === 'overview' || !this.hasOverviewConfiguration;
  }

  /** The organisation and the Seahorse object are picked. */
  get isModuleValid(): boolean {
    return (
      !!this.seahorseModule.organisationId && !!this.seahorseModule.mappingKey
    );
  }

  /** The current step may be left (or saved). */
  get isStepValid(): boolean {
    return this.step === 'module'
      ? this.isModuleValid
      : isOverviewConfigurationValid(this.seahorseModule.overviewConfiguration);
  }

  setOrganisation(organisationId: unknown) {
    this.seahorseModule.organisationId = (organisationId as string) ?? null;
    this.seahorseModule.mappingKey = null;
    this.seahorseObjects = [];

    if (!this.seahorseModule.organisationId) {
      return;
    }

    this._proxyService.organisationId = this.seahorseModule.organisationId;
    this._proxyService
      .getData()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        const definitions = this._proxyService.objectDefinitions ?? [];

        this.seahorseObjects = definitions
          .filter((x) => x.scope === ScopeType.Seahorse)
          .map((def) => ({
            ...def,
            name: this._translateService.instant(def.name),
          }));
      });
  }

  setMappingKey(mappingKey: unknown) {
    this.seahorseModule.mappingKey = (mappingKey as string) ?? null;
  }

  next() {
    if (!this.isStepValid || this.isLast) {
      return;
    }

    if (!this.seahorseModule.overviewConfiguration) {
      this.seahorseModule.overviewConfiguration =
        UiModuleOverviewConfiguration.default();
    }

    this.step = 'overview';
  }

  back() {
    this.step = 'module';
  }

  save() {
    if (!this.isStepValid || !this.isLast || this.saveStarted) {
      return;
    }

    if (this.hasOverviewConfiguration) {
      this.overviewForm?.prepareForSave();
    } else {
      this.seahorseModule.overviewConfiguration = null;
    }

    this.saveStarted = true;
    this._seahorseModuleService.add(this.seahorseModule).subscribe({
      next: (res) => {
        this.saveStarted = false;

        if (res?.hasResult && res.result) {
          this.dialogRef.ok(res.result);
          this._notificationService.showSuccess(
            this._translateService.instant('shared.terms.success')
          );
        } else {
          this.dialogRef.cancel();
          this._notificationService.showError(
            this._translateService.instant('shared.terms.failed')
          );
        }
      },
      error: () => {
        this.saveStarted = false;
        this.dialogRef.cancel();
        this._notificationService.showError(
          this._translateService.instant('shared.terms.failed')
        );
      },
    });
  }
}
